import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import { getFeatureGroupUsers, getUserCompanies } from '../services/featureService';
import { formatRelativeDate } from '../utils/dateUtils';
import AddUserPanel from './AddUserPanel';
import EditUserPanel from './EditUserPanel';
import './Users.css';

const Users = ({ featureGroupId, userRole }) => {
  const [users, setUsers] = useState([]);
  const [userCompanies, setUserCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const isOwner = userRole === 'Owner';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersData, companiesData] = await Promise.all([
        getFeatureGroupUsers(featureGroupId),
        getUserCompanies(featureGroupId)
      ]);
      setUsers(usersData);
      setUserCompanies(companiesData);
    } catch (err) {
      setError('Failed to load users');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureGroupId]);

  useEffect(() => {
    if (featureGroupId && isOwner) {
      loadData();
    }
  }, [featureGroupId, isOwner, loadData]);

  const getUserCompany = useCallback((userId) => {
    const userCompany = userCompanies.find(uc => uc.user_id === userId);
    return userCompany ? userCompany.company_name : 'No company assigned';
  }, [userCompanies]);

  const getUserCompanyId = useCallback((userId) => {
    const userCompany = userCompanies.find(uc => uc.user_id === userId);
    return userCompany ? userCompany.company_id : null;
  }, [userCompanies]);

  const handleEdit = (user) => {
    const companyId = getUserCompanyId(user.user_id);
    const userWithCompany = {
      ...user,
      company_id: companyId || ''
    };
    setEditingUser(userWithCompany);
  };

  const handleCloseEditPanel = () => {
    setEditingUser(null);
    loadData();
  };

  const handleAddUser = () => {
    setShowAddPanel(true);
  };

  const handleClosePanel = () => {
    setShowAddPanel(false);
    loadData();
  };

  if (!isOwner) {
    return (
      <div className="users-container">
        <div className="access-denied">
          <p>You must be an Owner to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <div className="users-header-content">
          <div>
            <h2>Users</h2>
            <p className="panel-description">Manage users and their roles in this feature map</p>
          </div>
          <button className="add-user-button" onClick={handleAddUser}>
            <i className="fas fa-plus"></i> Add User
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading users...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <div className="users-list">
          {users.length === 0 ? (
            <div className="no-users">
              No users in this feature map yet. Click "Add User" to get started.
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Company</th>
                  <th>Joined</th>
                  <th>Last Visit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.user_id}>
                    <td className="user-email">{user.email}</td>
                    <td>
                      <span className={`role-badge role-${(user.role || '').toLowerCase()}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="user-company">{getUserCompany(user.user_id)}</td>
                    <td className="user-joined">
                      {user.created_at 
                        ? new Date(user.created_at).toLocaleDateString() 
                        : 'N/A'}
                    </td>
                    <td className="user-last-visit">
                      {formatRelativeDate(user.last_visit)}
                    </td>
                    <td className="user-actions">
                      <button
                        className="icon-button"
                        onClick={() => handleEdit(user)}
                        title="Edit User"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="icon-button delete-button"
                        onClick={() => handleEdit(user)}
                        title="Edit/Delete User"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showAddPanel && (
        <AddUserPanel
          isOpen={showAddPanel}
          featureGroupId={featureGroupId}
          onClose={handleClosePanel}
          onSuccess={loadData}
        />
      )}

      {editingUser && (
        <EditUserPanel
          isOpen={!!editingUser}
          user={editingUser}
          featureGroupId={featureGroupId}
          onClose={handleCloseEditPanel}
          onSuccess={loadData}
        />
      )}
    </div>
  );
};

export default Users;

